import { FrameParser } from "./types"

export type Zoom = {
    framesPerIndex: number
    zoomLevel: number
    buffer: Uint32Array
    numIndexes: number
    currentIndex: number
}

const initialZoomSize = 1024

export const INDEX_SIZE_BYTES = 4096
export const INDEX_LENGTH_U32 = 1024
export const INDEXES_PER_TILE = 512
export const TILE_SIZE_U32 = INDEX_LENGTH_U32 * INDEXES_PER_TILE

export const getFramesPerZoom = (zoom: number, totalFrames: number) => {
    const numTiles = (initialZoomSize / INDEXES_PER_TILE) * Math.pow(2, zoom - 1)

    const framesPerSuperIndex = totalFrames / numTiles / INDEXES_PER_TILE

    return framesPerSuperIndex < 1 ? 1 : framesPerSuperIndex
}

export const calculateMaxZoom = (numFrames: number) =>
    Math.max(Math.ceil(Math.log2(numFrames / INDEXES_PER_TILE)), 1)

export class SuperIndexController {
    private maxZoom: number = -1
    private zooms: Zoom[] = []

    constructor(
        private parser: FrameParser,
        private numFrames: number
    ) {
        this.maxZoom = calculateMaxZoom(numFrames)

        const zooms = []
        for (let i = 1; i <= this.maxZoom; i++) {
            const framesPerIndex = getFramesPerZoom(i, numFrames)
            const indexesPerCollection = Math.min(numFrames, initialZoomSize * Math.pow(2, i - 1))
            zooms.push({
                buffer: new Uint32Array(indexesPerCollection * INDEX_LENGTH_U32),
                framesPerIndex: framesPerIndex,
                zoomLevel: i,
                numIndexes: indexesPerCollection,
                currentIndex: -1,
            } satisfies Zoom)
        }
        this.zooms = zooms
    }

    public getNumFrames() {
        return this.numFrames
    }

    public getMaxZoom() {
        return this.maxZoom
    }

    public getZoomBuffers() {
        return this.zooms.map((z) => z.buffer)
    }

    public getZooms() {
        return this.zooms
    }

    public getParser() {
        return this.parser
    }

    async parseColorspace(frame: VideoFrame, frameNumber: number) {
        if (!this.parser || this.maxZoom === -1 || this.numFrames === -1)
            throw new Error("parseColorspace called before running `init`")
        const index = await this.parser.parseColorspace(frame)
        this.processFrame(frameNumber, index)
    }

    processFrame(frameNumber: number, frame: Uint32Array) {
        for (let i = 0; i < this.zooms.length; i++) {
            const zoom = this.zooms[i]
            const index = Math.floor(frameNumber / this.zooms[i].framesPerIndex)

            if (index >= zoom.numIndexes) {
                continue
            }

            const offset = index * INDEX_LENGTH_U32

            for (let j = 0; j < INDEX_LENGTH_U32; j++) {
                zoom.buffer[offset + j] |= frame[j]
            }
        }
    }
}
